import { ref } from 'vue'

import { useToast } from '@/composables/useToast'
import api from '@/store/api'
import { AGENTS_REQUEST } from '@/view/ai/agentTypes'

const QUIET = { receive: false, save: false }
const GO_LIVE_ERROR = 'This agent could not go live. Try again.'

// The impact estimate of a saved agent; null when it cannot be estimated, so
// the warning still shows without a number (as AgentForm does).
export const fetchAgentImpact = agentId =>
  api
    .call('GET', `agents/${agentId}/impact`, {}, QUIET)
    .then(response => response.data?.data?.attributes || null)
    .catch(() => null)

// Going live changes nothing but test_mode.
export const setAgentLive = agentId => api.call('PUT', `agents/${agentId}`, { agent: { test_mode: false } })

export const reloadAgents = () => api.call('GET', AGENTS_REQUEST)

export function useAgentGoLive() {
  const { showToast } = useToast()
  const impactOpen = ref(false)
  const estimate = ref(null)
  const agentType = ref('')
  const saving = ref(false)
  let agentId = null

  const request = async (id, type = '') => {
    agentId = id
    agentType.value = type
    estimate.value = await fetchAgentImpact(id)
    impactOpen.value = true
  }

  const confirm = async () => {
    if (agentId === null || saving.value) return false
    saving.value = true
    try {
      await setAgentLive(agentId)
    } catch {
      showToast(GO_LIVE_ERROR, { type: 'error' })
      return false
    } finally {
      // The store is refreshed whatever happened; a failed refresh does not undo a live agent.
      await reloadAgents().catch(() => {})
      saving.value = false
    }
    impactOpen.value = false
    agentId = null
    showToast('Agent is live')
    return true
  }

  const cancel = () => {
    impactOpen.value = false
    agentId = null
  }

  return { impactOpen, estimate, agentType, saving, request, confirm, cancel }
}
